import { isNative } from 'src/utils/platform';
import { registerPlugin } from '@capacitor/core';
import { reportError } from 'src/modules/logs/Sentry';

interface FacebookSDKInitPlugin {
  setAutoLogAppEventsEnabled(options: { enabled: boolean }): Promise<void>;
  setAdvertiserIDCollectionEnabled(options: { enabled: boolean }): Promise<void>;
  setAdvertiserTrackingEnabled(options: { enabled: boolean }): Promise<void>;
  activateApp(): Promise<void>;
  logEvent(options: { eventName: string }): Promise<void>;
}

const FacebookSDKInit =
  registerPlugin<FacebookSDKInitPlugin>('FacebookSDKInitPlugin');

let isTrackingEnabled = false;
let hasActivatedApp = false;

let hasBeenConfigured = false;

export const disableFacebookTracking = async () => {
  if (!isNative()) return;
  try {
    await FacebookSDKInit.setAutoLogAppEventsEnabled({ enabled: false });
    await FacebookSDKInit.setAdvertiserIDCollectionEnabled({ enabled: false });
    await FacebookSDKInit.setAdvertiserTrackingEnabled({ enabled: false });
    isTrackingEnabled = false;
    hasBeenConfigured = true;
  } catch (error) {
    reportError('Error disabling Facebook tracking', error);
  }
};

export const enableFacebookTracking = async (
  attAuthorized: boolean = true,
) => {
  if (!isNative()) return;
  if (isTrackingEnabled) return;
  try {
    await FacebookSDKInit.setAdvertiserTrackingEnabled({
      enabled: attAuthorized,
    });
    await FacebookSDKInit.setAdvertiserIDCollectionEnabled({ enabled: true });
    await FacebookSDKInit.setAutoLogAppEventsEnabled({ enabled: true });
    if (!hasActivatedApp) {
      await FacebookSDKInit.activateApp();
      hasActivatedApp = true;
    }
    isTrackingEnabled = true;
    hasBeenConfigured = true;
  } catch (error) {
    reportError('Error enabling Facebook tracking', error);
  }
};

// Boot-tail-only: after a watchdog force-render, <AppsFlyer/> may already have disabled tracking for a
// cached child; a late enable would re-enable it (COPPA). Whichever caller configures first wins.
export const enableFacebookTrackingIfUnconfigured = async (
  attAuthorized: boolean = true,
) => {
  if (hasBeenConfigured) return;
  await enableFacebookTracking(attAuthorized);
};

export const logFacebookEvent = async (eventName: string) => {
  if (!isNative() || !isTrackingEnabled) return;
  return FacebookSDKInit.logEvent({ eventName });
};
